// Copyright 2018 Northern.tech AS
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

// material ui
import { List, ListItem, ListItemText, listClasses } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { getCurrentUser, getUserCapabilities } from '@northern.tech/store/selectors';

import { routeConfigs } from '../config/routes';
import { isRestrictedSelectGroup } from '../utils/selectGroupPolicy';

const listItems = [
  { ...routeConfigs.dashboard, canAccess: ({ userCapabilities: { SPTenant } }) => !SPTenant },
  { ...routeConfigs.devices, canAccess: ({ userCapabilities: { canReadDevices, SPTenant } }) => canReadDevices && !SPTenant },
  {
    ...routeConfigs.releases,
    canAccess: ({ userCapabilities: { canReadReleases, canUploadReleases, SPTenant } }) => (canReadReleases || canUploadReleases) && !SPTenant
  },
  {
    ...routeConfigs.deployments,
    canAccess: ({ userCapabilities: { canDeploy, canReadDeployments, SPTenant } }) => (canReadDeployments || canDeploy) && !SPTenant
  },
  { ...routeConfigs.deviceMonitor, canAccess: ({ userCapabilities: { SPTenant } }) => !SPTenant },
  { ...routeConfigs.tenants, canAccess: ({ userCapabilities: { SPTenant } }) => SPTenant },
  //{ ...routeConfigs.auditlog, canAccess: ({ userCapabilities: { canAuditlog } }) => canAuditlog }
];

const useStyles = makeStyles()(theme => ({
  list: {
    backgroundColor: theme.palette.background.lightgrey ? theme.palette.background.lightgrey : theme.palette.grey[100],
    borderRight: `1px solid ${theme.palette.divider}`,
    [`.${listClasses.root}`]: { paddingTop: 0 }
  },
  navLink: {
    padding: theme.spacing(3.5),
    color: theme.palette.text.primary,
    [`&.active`]: {
      backgroundColor: theme.palette.background.default,
      borderTop: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  }
}));

export const LeftNav = () => {
  const releasesRef = useRef();
  const { classes } = useStyles();

  const userCapabilities = useSelector(getUserCapabilities);
  const currentUser = useSelector(getCurrentUser);
  const isCurrentUserLoaded = Boolean(currentUser?.id);
  const shouldHideRestrictedMenus = !isCurrentUserLoaded || isRestrictedSelectGroup(currentUser?.select_group);

  return (
    <div className={`leftFixed leftNav flexbox column space-between ${classes.list}`}>
      <List>
        {listItems.reduce((accu, item, index) => {
          const isReleases = item.path === routeConfigs.releases.path;
          const isDeployments = item.path === routeConfigs.deployments.path;
          const isRestrictedMenuItem = isReleases || isDeployments;

          if (shouldHideRestrictedMenus && isRestrictedMenuItem) {
            return accu;
          }

          if (!item.canAccess({ userCapabilities })) {
            return accu;
          }

          accu.push(
            <ListItem
              className={`navLink leftNav ${classes.navLink} padding-left-large`}
              component={NavLink}
              end={item.path === ''}
              key={index}
              ref={isReleases ? releasesRef : null}
              to={`/${item.path}`}
            >
              <ListItemText primary={item.title} />
            </ListItem>
          );
          return accu;
        }, [])}
      </List>
    </div>
  );
};

export default LeftNav;
